import { useMemo, useState } from 'react';
import { AlertTriangle, Ban, Coins, Filter, MessagesSquare, Users } from 'lucide-react';
import { Combobox } from '@/shared/ui/combobox';
import { KpiCard } from '@/shared/components/kpi-card';
import { useCreditsOverview } from '../api/use-ai-admin';
import { formatTokens, formatUsd } from '../model/format';
import { CostBreakdownCard } from './cost-breakdown-card';
import { GlobalSettingsCard } from './global-settings-card';

const MONTHS_BACK = 12;

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function lastMonths(): Array<{ value: string; label: string }> {
  const now = new Date();
  return Array.from({ length: MONTHS_BACK }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return { value: monthKey(date), label: label.charAt(0).toUpperCase() + label.slice(1) };
  });
}

/** Gasto real com IA, onde foi parar e o padrão global — aba de Observabilidade. */
export function AiCostsPanel() {
  const months = useMemo(lastMonths, []);
  const [month, setMonth] = useState(months[0].value);
  const { data: overview, isLoading } = useCreditsOverview(month);
  const totals = overview?.totals;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="w-full sm:w-52">
          <Combobox value={month} onChange={setMonth} options={months} searchPlaceholder="Buscar mês" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          index={0}
          icon={Coins}
          label="Gasto no mês"
          value={formatUsd(totals?.costUsd)}
          loading={isLoading}
        />
        <KpiCard
          index={1}
          icon={Filter}
          label="Segmentações criadas"
          value={totals?.segmentations ?? 0}
          loading={isLoading}
        />
        <KpiCard
          index={2}
          icon={MessagesSquare}
          label="Conversas"
          value={totals?.sessions ?? 0}
          hint={totals ? `${totals.calls} chamadas ao modelo` : undefined}
          loading={isLoading}
        />
        <KpiCard
          index={3}
          icon={Users}
          label="Usuários ativos"
          value={totals?.activeUsers ?? 0}
          hint={
            totals
              ? `${formatTokens(totals.inputTokens + totals.outputTokens)} tokens no mês`
              : undefined
          }
          loading={isLoading}
        />
        <KpiCard
          index={4}
          icon={AlertTriangle}
          label="Perto do limite"
          value={totals?.nearLimitUsers ?? 0}
          loading={isLoading}
        />
        <KpiCard
          index={5}
          icon={Ban}
          label="Sem créditos"
          value={totals?.blockedUsers ?? 0}
          loading={isLoading}
        />
      </div>

      <GlobalSettingsCard />
      <CostBreakdownCard month={month} />
    </div>
  );
}
